/**
 * AIGuidanceContext — Makes AI guidance mode available throughout the app.
 * Components can adapt their behavior based on the user's preference.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAIGuidanceMode, type AIGuidanceMode } from "@/hooks/useAIGuidanceMode";

interface AIGuidanceContextValue {
  mode: AIGuidanceMode;
  isProactive: boolean;
  showAI: boolean;
  isCoaching: boolean;
  isLoading: boolean;
  setMode: ReturnType<typeof useAIGuidanceMode>["setMode"];
}

const AIGuidanceContext = createContext<AIGuidanceContextValue>({
  mode: "copilot",
  isProactive: true,
  showAI: true,
  isCoaching: true,
  isLoading: true,
  setMode: {} as ReturnType<typeof useAIGuidanceMode>["setMode"],
});

export function AIGuidanceProvider({ children }: { children: ReactNode }) {
  const { mode, setMode, isProactive, showAI, isCoaching, isLoading } = useAIGuidanceMode();

  const value = useMemo(
    () => ({ mode, setMode, isProactive, showAI, isCoaching, isLoading }),
    [mode, setMode, isProactive, showAI, isCoaching, isLoading],
  );

  return <AIGuidanceContext.Provider value={value}>{children}</AIGuidanceContext.Provider>;
}

export const useGuidance = () => useContext(AIGuidanceContext);
